import { html, nothing } from "lit";
import type { SessionTabActivityMap } from "../chat/session-tab-activity.ts";
import type { SessionsListResult } from "../types.ts";
import { isSubagentSessionKey } from "../../../../src/routing/session-key.js";
import {
  resolveChatTabKind,
  resolveChatTabLabelWithHints,
  type ChatTabLabelHint,
} from "../chat/chat-tab-labels.ts";
import { normalizeChatOpenTabs, resolveChatTabLabel } from "../chat/chat-tabs.ts";
import { classifySessionCatalogKind } from "../chat/session-catalog.ts";
import { CHAT_SESSION_TAB_HINTS } from "../control-hints.ts";
import { icons } from "../icons.ts";

/**
 * Per-tab sticky label memo for the chat tab row.
 *
 * The tab label resolves via `resolveChatTabLabelWithHints`, which falls
 * through to `resolveChatTabLabel` → `sessions.find(row.key === key).label`
 * when no `chatSubagents` hint is available. During a sessions-list
 * refetch, that row is briefly undefined or missing-label, so the chip
 * flashes between the user-supplied label and the bare session-key
 * fallback (`MAIN · fork`). JAISIU-3153.
 *
 * The map is keyed by the tab `sessionKey` and stores the *last non-fallback*
 * label so transient `undefined`/missing-label states can be replaced with
 * the sticky. Entries are evicted when the tab closes (`tabs` array
 * shrinks) so memory stays bounded.
 */
const stickyTabLabels = new Map<string, string>();

function stickyTabLabelMemo(tabs: string[], nextLabels: Record<string, string>): void {
  const tabSet = new Set(tabs);
  for (const [key, _value] of stickyTabLabels) {
    if (!tabSet.has(key)) {
      stickyTabLabels.delete(key);
    }
  }
  for (const [key, value] of Object.entries(nextLabels)) {
    if (value) {
      stickyTabLabels.set(key, value);
    }
  }
}

export type ChatSessionTabsProps = {
  activeSessionKey: string;
  openTabs: string[];
  sessions: SessionsListResult | null;
  labelHints?: Record<string, ChatTabLabelHint>;
  highlightSessionKey?: string | null;
  connected: boolean;
  onSelectTab: (sessionKey: string) => void;
  onCloseTab: (sessionKey: string) => void;
  onForkTab?: () => void;
  onResetTab?: () => void;
  onDeleteSession?: (sessionKey: string) => void;
  onReorderTabs?: (fromIndex: number, toIndex: number) => void;
  /** Tab row only; primary list is the Chats sidebar. */
  onManageSessions?: () => void;
  /** Accessible name for the tablist (row 2: open tabs only). */
  rowLabel?: string;
  tabActivity?: SessionTabActivityMap;
};

function tabKindLabel(kind: ChatTabLabelHint["kind"] | null): string | null {
  switch (kind) {
    case "subagent":
      return "Subagent";
    case "fork":
      return "Fork";
    case "main":
      return "Main";
    default:
      return null;
  }
}

function canDeleteSessionTab(sessionKey: string): boolean {
  return classifySessionCatalogKind(sessionKey) !== "main";
}

export function renderChatSessionTabs(props: ChatSessionTabsProps) {
  const tabs = normalizeChatOpenTabs(props.openTabs, props.activeSessionKey);
  const count = tabs.length;
  const hints = props.labelHints;
  const listLabel = props.rowLabel ?? `Open chat sessions (${count})`;
  // JAISIU-3153: pre-resolve every tab label using the sticky memo so a
  // transient sessions-list refetch doesn't flash the bare session-key
  // fallback. Then update the memo with whatever we resolved so the next
  // render keeps the same label stable.
  const tabLabels: Record<string, string> = {};
  for (let i = 0; i < tabs.length; i++) {
    const key = tabs[i];
    const hintLabel = hints?.[key]?.label?.trim();
    if (hintLabel) {
      tabLabels[key] = hintLabel;
      continue;
    }
    if (isSubagentSessionKey(key)) {
      tabLabels[key] = resolveChatTabLabelWithHints(key, i, props.sessions, hints);
      continue;
    }
    tabLabels[key] = resolveChatTabLabel(key, i, props.sessions, stickyTabLabels.get(key) ?? null);
  }
  stickyTabLabelMemo(tabs, tabLabels);

  // Drag-and-drop state (shared via closure)
  let dragFromIndex: number | null = null;
  let dragOverIndex: number | null = null;
  let dragInsertBefore: boolean | null = null;

  // Touch drag state
  let touchDragFromIndex: number | null = null;
  let touchStartTimer: ReturnType<typeof setTimeout> | null = null;
  let touchStartX = 0;
  let touchStartY = 0;
  let touchDragOverIndex: number | null = null;
  let touchDragInsertBefore: boolean | null = null;

  // Keyboard reorder state (shared via closure)
  let keyboardPickedIndex: number | null = null;
  let keyboardOverIndex: number | null = null;

  return html`
    <div
      class="chat-session-tabs fleet-session-bar__row fleet-session-bar__row--tabs"
      role="tablist"
      aria-label=${listLabel}
      @keydown=${(e: KeyboardEvent) => {
        if (!props.onReorderTabs) return;

        const picked = keyboardPickedIndex;
        const currentOver = keyboardOverIndex ?? picked ?? -1;

        if ((e.key === " " || e.key === "Enter") && picked === null) {
          // Pick up the active tab
          const activeIdx = tabs.findIndex((t) => t === props.activeSessionKey);
          if (activeIdx >= 0) {
            keyboardPickedIndex = activeIdx;
            keyboardOverIndex = activeIdx;
            e.preventDefault();
          }
        } else if ((e.key === " " || e.key === "Enter") && picked !== null) {
          // Drop the tab
          const dropIdx = keyboardOverIndex ?? picked;
          if (dropIdx !== picked) {
            props.onReorderTabs(picked, dropIdx);
          }
          keyboardPickedIndex = null;
          keyboardOverIndex = null;
          e.preventDefault();
        } else if (e.key === "ArrowLeft" && picked !== null) {
          // Move picked tab left
          const newIdx = Math.max(0, (keyboardOverIndex ?? picked) - 1);
          keyboardOverIndex = newIdx;
          e.preventDefault();
        } else if (e.key === "ArrowRight" && picked !== null) {
          // Move picked tab right
          const newIdx = Math.min(tabs.length - 1, (keyboardOverIndex ?? picked) + 1);
          keyboardOverIndex = newIdx;
          e.preventDefault();
        } else if (e.key === "Escape" && picked !== null) {
          // Cancel
          keyboardPickedIndex = null;
          keyboardOverIndex = null;
          e.preventDefault();
        }
      }}
    >
      <div class="chat-session-tabs__scroll">
        ${tabs.map((key, index) => {
          const active = key === props.activeSessionKey;
          const label = tabLabels[key];
          const kind = resolveChatTabKind(key, hints);
          const kindLabel = tabKindLabel(kind);
          const highlighted = props.highlightSessionKey === key;
          const activity = props.tabActivity?.[key];
          const canClose = count > 1;
          const canDelete = Boolean(props.onDeleteSession) && canDeleteSessionTab(key);
          return html`
            <div
              class="chat-session-tabs__tab ${active ? "chat-session-tabs__tab--active" : ""} ${highlighted ? "chat-session-tabs__tab--highlight" : ""} ${dragFromIndex === index ? "chat-session-tabs__tab--dragging" : ""} ${touchDragFromIndex === index ? "chat-session-tabs__tab--dragging" : ""} ${dragOverIndex === index && dragFromIndex !== index && dragInsertBefore === true ? "chat-session-tabs__tab--insertion-before" : ""} ${dragOverIndex === index && dragFromIndex !== index && dragInsertBefore === false ? "chat-session-tabs__tab--insertion-after" : ""} ${touchDragOverIndex === index && touchDragFromIndex !== index && touchDragInsertBefore === true ? "chat-session-tabs__tab--insertion-before" : ""} ${touchDragOverIndex === index && touchDragFromIndex !== index && touchDragInsertBefore === false ? "chat-session-tabs__tab--insertion-after" : ""} ${keyboardPickedIndex === index ? "chat-session-tabs__tab--keyboard-picked" : ""} ${keyboardOverIndex === index && keyboardPickedIndex !== index ? "chat-session-tabs__tab--keyboard-over" : ""}"
              role="presentation"
              data-session-key=${key}
              data-tab-index=${index}
              draggable=${props.onReorderTabs ? "true" : "false"}
              @dragstart=${(e: DragEvent) => {
                dragFromIndex = index;
                e.dataTransfer!.effectAllowed = "move";
                e.dataTransfer!.setData("text/plain", key);
              }}
              @dragover=${(e: DragEvent) => {
                if (props.onReorderTabs && dragFromIndex !== null && dragFromIndex !== index) {
                  e.preventDefault();
                  e.dataTransfer!.dropEffect = "move";
                  dragOverIndex = index;
                  // Determine insertion position based on mouse X within tab
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  const midX = rect.left + rect.width / 2;
                  dragInsertBefore = e.clientX < midX;
                }
              }}
              @dragleave=${(e: DragEvent) => {
                // Only clear dragOverIndex when leaving the tab entirely,
                // not when moving to child elements (buttons, etc.)
                const tabDiv = e.currentTarget as HTMLElement;
                if (!e.relatedTarget || !tabDiv.contains(e.relatedTarget as Node)) {
                  dragOverIndex = null;
                  dragInsertBefore = null;
                }
              }}
              @drop=${(e: DragEvent) => {
                e.preventDefault();
                if (props.onReorderTabs && dragFromIndex !== null && dragFromIndex !== index) {
                  // When dropping "after" the target, insert at index + 1
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  const midX = rect.left + rect.width / 2;
                  const insertBefore = e.clientX < midX;
                  const dropIndex = insertBefore ? index : index + 1;
                  props.onReorderTabs(dragFromIndex, dropIndex);
                }
                dragFromIndex = null;
                dragOverIndex = null;
                dragInsertBefore = null;
              }}
                      @dragend=${() => {
                        dragFromIndex = null;
                        dragOverIndex = null;
                        dragInsertBefore = null;
                      }}
              @touchstart=${(e: TouchEvent) => {
                if (!props.onReorderTabs) return;
                if (e.touches.length !== 1) return;
                touchStartX = e.touches[0].clientX;
                touchStartY = e.touches[0].clientY;
                const tabIndex = index;
                touchStartTimer = setTimeout(() => {
                  touchDragFromIndex = tabIndex;
                  touchStartTimer = null;
                  // Prevent text selection on touch drag
                  e.preventDefault();
                }, 300);
              }}
              @touchmove=${(e: TouchEvent) => {
                if (touchStartTimer) {
                  // Cancel long-press if finger moved too much before it fired
                  const dx = Math.abs(e.touches[0].clientX - touchStartX);
                  const dy = Math.abs(e.touches[0].clientY - touchStartY);
                  if (dx > 10 || dy > 10) {
                    clearTimeout(touchStartTimer);
                    touchStartTimer = null;
                  }
                  return;
                }
                if (touchDragFromIndex === null) return;
                e.preventDefault();

                // Find which tab is under the current touch point
                const touch = e.touches[0];
                const hovered = document.elementFromPoint(touch.clientX, touch.clientY);
                const hoveredTab = (hovered as HTMLElement | null)?.closest(
                  ".chat-session-tabs__tab",
                ) as HTMLElement | null;
                if (!hoveredTab) {
                  touchDragOverIndex = null;
                  touchDragInsertBefore = null;
                  return;
                }
                const hoveredIdx = parseInt(hoveredTab.dataset.tabIndex ?? "-1", 10);
                if (hoveredIdx >= 0 && hoveredIdx !== touchDragFromIndex) {
                  const rect = hoveredTab.getBoundingClientRect();
                  touchDragOverIndex = hoveredIdx;
                  touchDragInsertBefore = touch.clientX < rect.left + rect.width / 2;
                } else {
                  touchDragOverIndex = null;
                  touchDragInsertBefore = null;
                }
              }}
              @touchend=${(e: TouchEvent) => {
                if (touchStartTimer) {
                  clearTimeout(touchStartTimer);
                  touchStartTimer = null;
                }
                if (touchDragFromIndex !== null && touchDragOverIndex !== null) {
                  const fromIdx = touchDragFromIndex;
                  let toIdx = touchDragOverIndex;
                  // "After" offset: when inserting after tab N, target index is N+1
                  if (!touchDragInsertBefore) {
                    toIdx = touchDragOverIndex + 1;
                  }
                  if (toIdx !== fromIdx && toIdx !== fromIdx + 1) {
                    props.onReorderTabs?.(fromIdx, toIdx);
                  }
                }
                touchDragFromIndex = null;
                touchDragOverIndex = null;
                touchDragInsertBefore = null;
              }}
              @touchcancel=${() => {
                if (touchStartTimer) {
                  clearTimeout(touchStartTimer);
                  touchStartTimer = null;
                }
                touchDragFromIndex = null;
                touchDragOverIndex = null;
                touchDragInsertBefore = null;
              }}
            >
              <button
                type="button"
                class="chat-session-tabs__select"
                role="tab"
                aria-selected=${active}
                title=${active ? `Active tab: ${label} (${key})` : `Switch to ${label} (${key})`}
                aria-label=${`${label}${kindLabel ? `, ${kindLabel}` : ""}${active ? ", active" : ""}`}
                @click=${() => props.onSelectTab(key)}
              >
                ${
                  kindLabel
                    ? html`<span class="chat-session-tabs__kind chat-session-tabs__kind--${kind ?? "main"}">${kindLabel}</span>`
                    : nothing
                }
                <span class="chat-session-tabs__label">${label}</span>
                ${
                  activity?.running
                    ? html`
                        <span class="chat-session-tabs__running" title="Run in progress"></span>
                      `
                    : nothing
                }
                ${
                  activity?.unread && !active
                    ? html`<span class="chat-session-tabs__unread" title="New messages">${activity.unread > 9 ? "9+" : activity.unread}</span>`
                    : nothing
                }
              </button>
              ${
                canDelete
                  ? html`
                    <button
                      type="button"
                      class="chat-session-tabs__delete"
                      title=${CHAT_SESSION_TAB_HINTS.deleteSession}
                      aria-label=${`Delete session ${label}`}
                      ?disabled=${!props.connected}
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        void props.onDeleteSession?.(key);
                      }}
                    >
                      ${icons.trash}
                    </button>
                  `
                  : nothing
              }
              ${
                canClose
                  ? html`
                    <button
                      type="button"
                      class="chat-session-tabs__close"
                      title=${CHAT_SESSION_TAB_HINTS.closeTab}
                      aria-label=${`Close tab ${label}`}
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        props.onCloseTab(key);
                      }}
                    >
                      ${icons.x}
                    </button>
                  `
                  : nothing
              }
            </div>
          `;
        })}
      </div>
      ${
        // JAISIU-3080: removed Fork and Reset buttons (the right-side
        // actions row was empty after this change; the empty <div> wrapper
        // is also dropped to avoid leaving a styled placeholder).
        nothing
      }
    </div>
  `;
}

/** Scroll the highlighted tab into view after opening a subagent session. */
export function scrollChatSessionTabIntoView(sessionKey: string) {
  requestAnimationFrame(() => {
    const el = document.querySelector(
      `.chat-session-tabs__tab[data-session-key="${CSS.escape(sessionKey)}"]`,
    );
    el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  });
}
